<template>
  <div class="bg-white h-auto px-5 rounded-lg">
    <div
      class="flex flex-col lg:flex-row md:items-center lg:gap-2 border-b-[1.5px] border-primary-purple-100 py-5"
    >
      <div class="flex items-center w-full justify-between">
        <div class="flex items-center">
          <Heading :heading="graphHeading" heading-size="text-[18px]" heading-padding="py-0" />
          <UiOnClickOutside :do="closeToolTip">
            <span
              class="ml-2 rtl:mr-2 cursor-pointer relative"
              @click="clickToolTip"
              @mouseover="displayToolTip"
              @mouseleave="hideToolTip"
            >
              <icon
                class="icon"
                icon="infoCircleUpdated"
                color="primary-purple-600"
                height="20.617"
                width="16.5"
              />
              <div
                v-if="showToolTip"
                class="absolute -ml-4 mt-3 md:mt-2 rtl:-mr-24 md:rtl:-mr-7 z-40"
              >
                <Tooltip
                  :title="$t(`toolTipInfo.${sectionName}`)"
                  text="Graph shows first section attendance."
                  direction="left"
                />
              </div>
            </span>
          </UiOnClickOutside>
        </div>

        <div
          v-permission="SECTION_ATTENDANCE_FILTERS_PERMISSION"
          class="xl:h-11 md:h-10 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border border-primary-gray-300 rounded-lg px-2"
        >
          <icon icon="filter" height="20" width="20" color="primary-gray-700" />
          <span class="font-inter font-semibold text-primary-gray-700 text-sm hidden sm:block">
            Filters
          </span>
        </div>
      </div>
      <div
        class="w-full lg:w-fit flex flex-wrap-reverse sm:flex-nowrap gap-2 items-end justify-end"
      >
        <UiFilterBox
          v-model="selectedCampus"
          :options="campusList"
          :is-switch-box="true"
          :is-disabled="isCampusDisabled"
          label="title"
          reduce="id"
        />
        <CalendarActionsWrapper
          :calendar-date="dateRange"
          :is-concise-view="true"
          :is-future-date-disabled="true"
          filter-view="Week"
          custom-class="right-0 before:right-12"
          @selectDate="updateSelectedDate"
          @selectDateRange="updateDateRange"
        />
      </div>
    </div>
    <div class="overflow-auto">
      <NoRecordFound v-if="isEmptyGraph" />
      <GraphView v-else :chart-data="graphData" class="min-w-7" />
    </div>
    <div class="pb-6 flex justify-between mt-2 w-full">
      <UiFilterBox
        v-model="selectedClass"
        :options="classList"
        :is-disabled="isClassDisabled"
        label="title"
        class="md:justify-end"
        reduce="id"
      />
      <UiDashboardStats title="Total Students:" :value="totalStudents" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import Heading from '@/src/components/Heading.vue'
import GraphView from '@src/router/views/graph/graphView.vue'
import UiDashboardStats from '@/src/components/UiElements/UiStatsCountDisplay.vue'
import UiFilterBox from '@/src/components/UiElements/UiFilterBox.vue'
import CalendarActionsWrapper from '@src/components/Calendar/CalendarActionsWrapper.vue'
import icon from '@src/components/icons/icon.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import { useStore } from 'vuex'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import {
  generateDayNames,
  generateFormattedDatesArray,
  getBackDate,
  formatDateFromLocalToUTC,
} from '@src/utils/moment.util.js'
import moment from 'moment'
import DATETIME_CONSTANT from '@src/constants/date-time-constants.js'
import Tooltip from '@components/tooltip.vue'
import UiOnClickOutside from '@components/UiElements/UiOnClickOutside.vue'
import tooltipMixin from '@src/mixins/components/tooltip-mixin.js'
import { pluralToSingular } from '@/src/utils/generalUtil'
import { ENV } from '@src/constants/general-constants.js'
defineOptions({
  mixins: [tooltipMixin],
})
const store = useStore()
const dateRange = ref({
  startDate: getBackDate(moment().toDate(), 6, 'days'),
  endDate: moment().toDate(),
})
const selectedCampus = ref('')
const selectedClass = ref('')
const classList = ref([])
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const currentClassScope = computed(() => store.state.layout.currentClassScope)
const currentSectionScope = computed(() => store.state.layout.currentSectionScope)
const isCampusDisabled = computed(() => !!currentCampusScope.value)
const isClassDisabled = computed(() => !!currentClassScope.value)
const campusList = computed(() => store.state.layout.campuses)
const weekNames = computed(() => generateDayNames(dateRange.value))
const dayNames = computed(() => generateFormattedDatesArray(dateRange.value))
const sectionName = computed(() => store.state.featureNaming.routerList.SECTIONS.DISPLAY_NAME)
const isEmptyGraph = computed(() => {
  return graphData.value.data?.datasets?.every(
    ({ data }) => !data?.length || data.every((item) => !item),
  )
})

const graphHeading = `${pluralToSingular(sectionName.value)} Attendance`
const totalStudents = ref(0)
const color = ref([
  '#991AFF',
  '#FF6633',
  '#00B3E6',
  '#E6B333',
  '#3366E6',
  '#B34D4D',
  '#809900',
  '#FF1A66',
  '#E6331A',
  '#4DB3FF',
  '#1AB399',
  '#E666B3',
  '#33991A',
])

const SECTION_ATTENDANCE_FILTERS_PERMISSION = {
  env: [ENV.DEV],
}
const graphData = ref({
  type: 'bar',
  tooltipHeader: [],
  data: {
    labels: weekNames.value,
    datasets: [],
  },
  options: {
    responsive: true,
    maxBarThickness: 8,
    maintainAspectRatio: false,
    barPercentage: 0.6,
    categoryPercentage: 0.2,
    aspectRatio: 100 / 80,
    borderRadius: {
      topLeft: 10,
      topRight: 10,
      bottomLeft: 0,
      bottomRight: 0,
    },
    borderSkipped: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        border: {
          display: false,
        },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'index',
    },
    plugins: {
      tooltip: { backgroundColor: 'white', titleColor: 'black', bodyColor: '#667085' },
      legend: {
        display: false,
      },
    },
  },
})
onMounted(() => {
  if (currentCampusScope.value) selectedCampus.value = currentCampusScope.value.id
  if (currentClassScope.value) selectedClass.value = currentClassScope.value.id
})
watch(currentCampusScope, async (newCampus, oldCampus) => {
  if (newCampus === oldCampus) return
  if (currentCampusScope.value) selectedCampus.value = currentCampusScope.value.id
})
watch(currentSectionScope, async () => {
  await generateSectionAttendanceStats()
})
watch(selectedCampus, async (newCampus, oldCampus) => {
  if (newCampus === oldCampus) return
  selectedCampus.value = newCampus
  await fetchClassList(newCampus)
})

watch(selectedClass, async (newClass, oldClass) => {
  if (newClass === oldClass) return
  selectedClass.value = newClass
  await generateSectionAttendanceStats()
})
watch(dateRange, async (newDate, oldDate) => {
  if (newDate === oldDate) return
  await generateSectionAttendanceStats()
})
const fetchClassList = async () => {
  let query = {
    $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', selectedCampus.value),
  }

  const response = await getClasses(query)
  if (response) classList.value = response
}
const generateSectionAttendanceStats = async () => {
  const payload = {
    $where: {
      ...buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', selectedCampus.value),
      ...buildWhereQuery(FILTER_KEYS.EQUAL, 'class_id', selectedClass.value),
    },
    from: formatDateFromLocalToUTC(dateRange.value.startDate, DATETIME_CONSTANT.DATE),
    to: formatDateFromLocalToUTC(dateRange.value.endDate, DATETIME_CONSTANT.DATE),
  }
  await getSectionAttendanceStats(payload).then((response) => {
    graphData.value.data.datasets = transformWeeklyStatsToChartData(response.weekly_stats)
    totalStudents.value = response.students_strength
    graphData.value.tooltipHeader = dayNames.value
  })
  updateDateRange(dateRange.value)
}
const transformWeeklyStatsToChartData = (weekly_stats) => {
  if (currentSectionScope.value) {
    const section = weekly_stats[0]
    const totalPresents = section.section_stat.map((stat) => stat.total_present)
    const totalAbsents = section.section_stat.map((stat) => stat.total_absent)

    return [
      { label: 'Present', backgroundColor: '#7F56D9', data: totalPresents },
      { label: 'Absent', backgroundColor: '#D6BBFB', data: totalAbsents },
    ]
  } else {
    return weekly_stats.map((section, index) => {
      const totalPresents = section.section_stat.map((stat) => stat.total_present)
      return {
        label: section.section_title || `Section ${index + 1}`,
        backgroundColor: color.value[index],
        data: totalPresents,
      }
    })
  }
}
const updateDateRange = (week) => {
  dateRange.value = week
}

const updateSelectedDate = (date) => {
  dateRange.value.startDate = date
}
const getClasses = (payload) => store.dispatch('classes/getUserClasses', payload)
const getSectionAttendanceStats = (payload) =>
  store.dispatch('instituteCount/getSectionAttendanceStats', payload)
</script>
